import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Section, Tile, Chip } from "@/growth/components/_ui";
import * as gc from "@/growth/api";

export default function VisibilityTab({ wsId, onChanged }) {
  const [candidates, setCandidates] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const load = async () => {
    const r = await gc.listVisAnswers(wsId);
    setPrompts(r.prompts || []);
    setAnswers(r.answers || []);
    setAggregates(r.aggregates || null);
  };
  useEffect(() => { load(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doGenerate = async () => {
    setBusy(true);
    try {
      const r = await gc.generateVisPrompts(wsId);
      setCandidates((r.prompts || []).map((p) => ({ ...p, keep: true })));
      toast.success(`Drafted ${r.prompts?.length || 0} candidate questions — review before saving.`);
    } catch { toast.error("Prompt draft failed"); }
    finally { setBusy(false); }
  };

  const doSave = async () => {
    const kept = candidates.filter((p) => p.keep && p.text);
    if (!kept.length) { toast.error("Select at least one prompt"); return; }
    setBusy(true);
    try {
      await gc.saveVisPrompts(wsId, kept);
      toast.success(`Approved ${kept.length} prompts.`);
      setCandidates([]);
      await load();
      onChanged?.();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const doRun = async (pid) => {
    setRunningId(pid);
    try {
      await gc.runVisPrompt(wsId, pid);
      toast.success("Captured answers from 3 engines.");
      await load();
      onChanged?.();
    } catch { toast.error("Run failed"); }
    finally { setRunningId(null); }
  };

  return (
    <div>
      <Section
        title="AI Visibility Monitor"
        subtitle="Track whether AI answer engines mention, cite, or recommend you for the questions your buyers actually ask. Approved prompts are automatically re-run every Monday 08:00 UTC so mention-rate trends update on their own."
        right={<Button variant="outline" onClick={doGenerate} disabled={busy} className="rounded-full" data-testid="gc-vis-generate">{busy ? "Working…" : "Draft candidate prompts"}</Button>}
      >
        {aggregates && aggregates.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Tile label="Observed answers" value={aggregates.total} tone="indigo" />
            <Tile label="Mention rate" value={`${Math.round(aggregates.mention_rate * 100)}%`} tone={aggregates.mention_rate > 0.5 ? "emerald" : "amber"} />
            <Tile label="Recommendation rate" value={`${Math.round(aggregates.recommend_rate * 100)}%`} tone={aggregates.recommend_rate > 0.3 ? "emerald" : "rose"} />
          </div>
        )}

        {candidates.length > 0 && (
          <Card className="p-5 border-2 border-emerald-200 rounded-2xl mb-4 bg-emerald-50/40">
            <div className="font-bold text-slate-900 mb-2">Review candidate questions</div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {candidates.map((p, i) => (
                <label key={i} className="flex items-start gap-3 text-sm">
                  <input type="checkbox" checked={p.keep} onChange={(e) => setCandidates(candidates.map((x, j) => j === i ? { ...x, keep: e.target.checked } : x))} className="mt-1" />
                  <div className="flex-1">
                    <div className="text-slate-800">{p.text}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.intent} · {p.market} · {p.language}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button onClick={doSave} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="gc-vis-save-prompts">Approve selected</Button>
              <Button variant="outline" onClick={() => setCandidates([])}>Discard</Button>
            </div>
          </Card>
        )}

        <div className="space-y-3">
          <div className="text-sm font-bold text-slate-600 uppercase tracking-widest">Approved Prompts</div>
          {prompts.length === 0 && <p className="text-sm text-slate-500">No approved prompts yet. Draft some above.</p>}
          {prompts.map((p) => (
            <Card key={p.id} className="p-4 border border-slate-200 rounded-xl" data-testid={`gc-vis-prompt-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{p.text}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.intent} · {p.market} · {p.language}
                    {p.last_auto_run_at && <> · Auto-run {new Date(p.last_auto_run_at).toLocaleDateString()}</>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => doRun(p.id)} disabled={runningId === p.id} className="rounded-full" data-testid={`gc-vis-run-${p.id}`}>
                  {runningId === p.id ? "Running…" : "Run now"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {answers.length > 0 && (
        <Section title="Observed Answers" subtitle="Every observation is evidence — see the full response, competitors, and citations.">
          <div className="space-y-3">
            {answers.slice(0, 12).map((a) => (
              <Card key={a.id} className="p-5 border border-slate-200 rounded-2xl" data-testid={`gc-vis-answer-${a.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">{a.engine}{a.auto_run ? " · auto" : ""}</div>
                    <div className="font-semibold text-slate-900">Q: {a.prompt_text}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Chip tone={a.brand_mentioned ? "emerald" : "rose"}>{a.brand_mentioned ? "Mentioned" : "Not mentioned"}</Chip>
                    {a.brand_recommended && <Chip tone="emerald">Recommended</Chip>}
                    <Chip tone={a.sentiment === "positive" ? "emerald" : a.sentiment === "negative" ? "rose" : "slate"}>{a.sentiment}</Chip>
                  </div>
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-line">{a.answer}</div>
                {(a.competitors_mentioned?.length > 0 || a.citations?.length > 0) && (
                  <div className="mt-3 text-xs text-slate-500 space-y-1">
                    {a.competitors_mentioned?.length > 0 && <div><strong>Competitors named:</strong> {a.competitors_mentioned.join(", ")}</div>}
                    {a.citations?.length > 0 && <div><strong>Cited:</strong> {a.citations.map((c) => c.domain || c.note).join(", ")}</div>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
